import { XMarkIcon } from "@heroicons/react/24/solid";
import { Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { updateHomepageViewResumeDialog } from "../../../redux/actions/dialog_action";
import {
  ResumeButton,
  ResumeButtonContainer,
  ResumeContainer,
  ResumeFilename,
  ResumeStatusChip,
  ResumeStatusChipText,
  ResumeStatusContainer,
  ResumeWrapper,
} from "../../homepage/HomepageResumeUploadedDialog/styles";

function getResumeName(resumeUrl) {
  const filename = resumeUrl?.split("/").pop()?.split("?")[0];

  if (!filename) return "";

  try {
    return decodeURIComponent(filename);
  } catch {
    return filename;
  }
}

function DropCvExistingResume({
  user,
  inputFileRef,
  onFileChange,
  onReupload,
  onClose,
  onSubmit,
  isSubmitting,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const statusKey =
    user?.state === "approved"
      ? "approved"
      : user?.state === "rejected"
      ? "rejected"
      : "in_review";

  function handleViewResume() {
    dispatch(updateHomepageViewResumeDialog(true));
  }

  return (
    <Box
      sx={{
        width: "100%",
        p: { xs: 2, md: "16px 24px" },
        background: "#FFFFFF",
        borderRadius: "16px",
        outline: "1.5px solid #222222",
        outlineOffset: "-1.5px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          mb: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              color: "#222222",
              fontFamily: "Inter, sans-serif",
              fontSize: "20px",
              fontWeight: 700,
              lineHeight: "24px",
              mb: 1,
            }}
          >
            {t("companydetail.work_here_resume_title")}
          </Box>
        </Box>

        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={onClose}
          sx={{
            width: 24,
            height: 24,
            minWidth: 24,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            p: 0,
          }}
        >
          <XMarkIcon width="24px" height="24px" />
        </Box>
      </Box>

      <Box
        sx={{
          color: "#222222",
          fontFamily: "Inter, sans-serif",
          fontSize: "14px",
          lineHeight: "20px",
          width: "100%",
          textAlign: "justify",
          textAlignLast: "left",
          mb: 2,
        }}
      >
        {t("companydetail.work_here_resume_description")}
      </Box>

      <Box
        sx={{
          fontFamily: "Inter, sans-serif",
          fontSize: "12px",
          fontWeight: 700,
          mb: 1,
        }}
      >
        {t("companydetail.work_here_resume_label")}
      </Box>

      <ResumeWrapper>
        <ResumeContainer sx={{ "&&": { gap: "8px", minWidth: 0 }}}>
          <ResumeStatusContainer sx={{ "&&": { minWidth: 0, maxWidth: "none" }}}>
            <ResumeFilename>{getResumeName(user?.resume)}</ResumeFilename>

            <ResumeStatusChip>
              <ResumeStatusChipText>
                {t(`companydetail.work_here_resume_status_${statusKey}`)}
              </ResumeStatusChipText>
            </ResumeStatusChip>
          </ResumeStatusContainer>

          <ResumeButtonContainer sx={{ "&&": { flexShrink: 0, gap: "8px" }}}>
            <ResumeButton
              type="button"
              buttonType="view"
              onClick={handleViewResume}
              sx={{
                "&&": {
                  width: "80px",
                  minWidth: "80px",
                  padding: "0 12px",
                  height: "30px",
                  fontSize: "12px",
                  fontWeight: 600,
                  lineHeight: 1,
                },
              }}
            >
              {t("companydetail.work_here_view_resume")}
            </ResumeButton>

            <ResumeButton
              type="button"
              buttonType="reupload"
              onClick={onReupload}
              sx= {{
                "&&": {
                  width: "88px",
                  minWidth: "88px",
                  padding: "0 12px",
                  height: "30px",
                  fontSize: "12px",
                  fontWeight: 600,
                  lineHeight: 1,
                },
              }}
            >
              {t("companydetail.work_here_reupload_resume")}
            </ResumeButton>
          </ResumeButtonContainer>
        </ResumeContainer>
      </ResumeWrapper>

      <Box
        sx={{
          color: "#707070",
          fontFamily: "Inter, sans-serif",
          fontSize: "10px",
          fontStyle: "italic",
          mt: 1,
        }}
      >
        {t("companydetail.work_here_reupload_note")}
      </Box>

      <input
        ref={inputFileRef}
        type="file"
        accept=".pdf,.doc,.docx"
        onChange={onFileChange}
        style={{ display: "none" }}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: "1px solid #D8D8D8",
          mt: 2,
          pt: 2,
        }}
      >
        <Button
          type="button"
          onClick={onClose}
          sx={{
            color: "#222222",
            fontFamily: "Inter",
            fontWeight: 700,
            textTransform: "none",
          }}
        >
          {t("companydetail.work_here_back")}
        </Button>

        <Button
          type="button"
          disabled={!onSubmit || isSubmitting}
          onClick={onSubmit}
          sx={{
            minWidth: { xs: "120px", sm: "150px" },
            height: "40px",
            fontSize: "14px",
            lineHeight: 1,
            color: "#FFFFFF",
            background: "#222222",
            borderRadius: "100px",
            fontFamily: "Inter",
            fontWeight: 700,
            textTransform: "none",

            "&:hover": {
              background: "#000000",
            },
          }}
        >
          {t("companydetail.work_here_submit")}
        </Button>
      </Box>
    </Box>
  );
}

export default DropCvExistingResume;
